import {
  useJSRuntime,
  usePythonRuntime,
} from '@/components/codeEditor/CodeEditor.hooks';
import { setEditorTheme } from '@/components/codeEditor/CodeEditorConfig';
import { Themes } from '@/data/context/ConfigContext';
import { LanguageCodeEditorEnum } from '@/models/CodeEditorActivity';
import CodeEditorBlockTool, {
  CodeEditorBlockConfig,
} from '@/utils/editorjs/code-editor';
import {
  EditorCallbacks,
  ICodeEditorFactory,
} from '@/utils/editorjs/code-editor/interfaces/CodeEditorFactory';
import { CodeEditorBlockToolData } from '@/utils/editorjs/code-editor/types/data';
import { ToolSettings } from '@editorjs/editorjs';
import { Monaco } from '@monaco-editor/loader';
import { editor } from 'monaco-editor';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useConfigContext from '../config';
import { useMonaco } from '@monaco-editor/react';

export default function useCodeEditorTool() {
  const [t] = useTranslation('translation', {
    keyPrefix: 'codeEditorLanguage',
  });
  const monaco = useMonaco();
  const { userSettings } = useConfigContext();
  const { run: runPython, loading: loadingPython } = usePythonRuntime({
    enabled: true,
  });
  const [isLoadingEditor, setIsLoadingEditor] = useState(!monaco);
  const { run: runJavascript, loading: loadingJavascript } = useJSRuntime();

  const [config, setConfig] = useState<Partial<CodeEditorBlockConfig>>({
    languages: Object.keys(LanguageCodeEditorEnum).map(key => ({
      label: t(key),
      value: key,
    })),
    callbacks: {},
    getEditorFactory: monaco
      ? () => new CodeEditorFactory(monaco, userSettings?.theme || 'light')
      : undefined,
  });

  const tool: ToolSettings = {
    class: CodeEditorBlockTool,
    config,
  };

  useEffect(() => {
    if (tool.config.getEditorFactory) return;
    if (!monaco) return;
    setConfig(prev => ({
      ...prev,
      getEditorFactory: () =>
        new CodeEditorFactory(monaco, userSettings?.theme || 'light'),
    }));
    setIsLoadingEditor(false);
  }, [tool.config.getEditorFactory, userSettings?.theme, monaco]);

  const runCode = useCallback(
    async (code: string, language: LanguageCodeEditorEnum) => {
      type RunnersMap = Record<
        LanguageCodeEditorEnum,
        (code: string) => Promise<string>
      >;
      const runners: RunnersMap = {
        [LanguageCodeEditorEnum.JAVASCRIPT]: runJavascript,
        [LanguageCodeEditorEnum.PYTHON]: runPython,
      };
      const runner =
        runners[language] || runners[LanguageCodeEditorEnum.JAVASCRIPT];
      const result = await runner(code);
      return result;
    },
    [runJavascript, runPython],
  );

  useEffect(() => {
    if (loadingPython || loadingJavascript) return;
    if (tool.config.callbacks.onRunCode) return;

    setConfig(prev => ({
      ...prev,
      callbacks: {
        onRunCode: (code: string, language: string) =>
          runCode(code, language as LanguageCodeEditorEnum),
      },
    }));
  }, [
    loadingPython,
    loadingJavascript,
    runCode,
    tool.config.callbacks.onRunCode,
  ]);

  const isLoadingLanguages = loadingJavascript || loadingPython;
  const isLoadingRunner = !tool.config.callbacks.onRunCode;

  const isLoading = isLoadingEditor || isLoadingLanguages || isLoadingRunner;

  return { isLoading, tool };
}

class CodeEditorFactory implements ICodeEditorFactory {
  private readonly monaco: Monaco;
  private manacoEditorElement: HTMLDivElement;
  private monacoEditor: editor.IStandaloneCodeEditor;

  constructor(monaco: Monaco, private readonly theme: Themes) {
    this.monaco = monaco;
    const editor = this.createEditorElement();
    const monacoEditor = this.createMonacoEditor(editor);
    this.manacoEditorElement = editor;
    this.monacoEditor = monacoEditor;
  }

  private createEditorElement(): HTMLDivElement {
    const editor = document.createElement('div');
    editor.classList.add('h-72');
    editor.style.width = '100%';
    editor.addEventListener('keydown', e => e.stopPropagation());
    return editor;
  }

  private createMonacoEditor(
    editorContainer: HTMLDivElement,
  ): editor.IStandaloneCodeEditor {
    const editor = this.monaco.editor.create(editorContainer, {
      automaticLayout: true,
      minimap: { enabled: false },
      cursorBlinking: 'solid',
    });

    setEditorTheme(this.monaco, this.theme);

    return editor;
  }

  private setMonacoEditorLanguage(language: string): void {
    const editorModel = this.monacoEditor.getModel();
    if (!editorModel) return;
    this.monaco.editor.setModelLanguage(editorModel, language.toLowerCase());
  }

  public createCodeEditorElement(
    data: CodeEditorBlockToolData,
    { onChangeCode }: EditorCallbacks = {},
  ): HTMLElement {
    this.monacoEditor.onDidChangeModelContent(() => {
      onChangeCode?.(this.monacoEditor.getValue());
    });

    this.updateLanguage(data.language || LanguageCodeEditorEnum.JAVASCRIPT);
    const editorModel = this.monacoEditor.getModel();
    editorModel?.setValue(data.code || '');
    return this.manacoEditorElement;
  }

  updateLanguage(language: string): void {
    this.setMonacoEditorLanguage(language);
  }
}
